import React, { useState } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { Merge } from 'type-fest';
import { cm } from 'src/utils/classNames';
import { getIsMediaHover } from 'src/utils/browser';
import { Z_INDEX } from 'src/constants/zIndex';
import {
	TOOLTIP_CONTENT_CLASSNAME,
	TOOLTIP_TRIGGER_DECORATION_CLASSNAME,
} from 'src/components/primitives/Tooltip/consts';
import { ConditionalAsChild } from 'src/components/primitives/ConditionalAsChild';
import TapOverlay from 'src/components/primitives/TapOverlay';
import { Info } from '@velocity-exchange/icons';

export interface TooltipProps extends Merge<
	RadixTooltip.TooltipProps,
	Pick<
		RadixTooltip.TooltipContentProps,
		| 'asChild'
		| 'aria-label'
		| 'onEscapeKeyDown'
		| 'onPointerDownOutside'
		| 'forceMount'
		| 'side'
		| 'align'
		| 'alignOffset'
		| 'avoidCollisions'
		| 'collisionBoundary'
		| 'collisionPadding'
		| 'sticky'
		| 'hideWhenDetached'
	>
> {
	/**
	 * The trigger for the tooltip. Radix passes props to it, so ensure that you
	 * spread all props to your component if you also pass `asChild`,
	 * otherwise things won't work correctly.
	 *
	 * If you do not pass `children`, it will render an `Info` icon by default.
	 */
	children?: React.ReactNode;
	/**
	 * Styles for the trigger element.
	 */
	className?: string;
	/**
	 * Content of the tooltip.
	 */
	content: React.ReactNode;
	/**
	 * Styles for the tooltip content.
	 */
	contentClassName?: string;
	/**
	 * If `none`, the trigger will not have default underline and help cursor applied.
	 * This is useful if you're adding a tooltip to something like a button.
	 */
	decoration?: 'default' | 'cursor-help' | 'none';
	/**
	 * This renders `children` directly in the `Trigger` instead of within a wrapper element.
	 * Note, `children` should be a single React element and it should spread props.
	 */
	asChild?: boolean;
	/**
	 * If true, will render `children` directly.
	 */
	disabled?: boolean;
	hideArrow?: boolean;
}

export default function Tooltip({
	children,
	content,
	defaultOpen,
	disabled,
	className,
	decoration = 'default',
	contentClassName,
	asChild,
	delayDuration = 200,
	open: openFromProps,
	onOpenChange: onOpenChangeFromProps,
	hideArrow,
	disableHoverableContent,
	...rest
}: TooltipProps) {
	const [openFromState, onOpenChangeFromState] = useState(!!defaultOpen);

	if (disabled) {
		return (
			<ConditionalAsChild
				className={className}
				asChild={asChild}
				fallback="span"
			>
				{children}
			</ConditionalAsChild>
		);
	}

	// Allow the open state to be controlled by the consumer if necessary.
	const open = openFromProps ?? openFromState;
	const onOpenChange = onOpenChangeFromProps ?? onOpenChangeFromState;

	const isMediaHover = getIsMediaHover();

	// If we're on a non-hover capable device, we allow opening via click.
	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (!isMediaHover) {
			e.preventDefault();
			onOpenChange(true);
		}
	};

	return (
		// Note, the `Provider` is not rendered at the root layout as it's known to cause issues.
		// See https://github.com/radix-ui/primitives/discussions/2183 for more details.
		<RadixTooltip.Provider>
			<RadixTooltip.Root
				open={open}
				onOpenChange={onOpenChange}
				delayDuration={!isMediaHover ? 0 : delayDuration}
				disableHoverableContent={disableHoverableContent}
			>
				<RadixTooltip.Trigger
					className={cm(
						decoration === 'default' && TOOLTIP_TRIGGER_DECORATION_CLASSNAME,
						decoration === 'cursor-help' && 'cursor-help',
						// If we're rendering the `Info` icon, give it flex so it centers itself.
						!children && 'inline-flex',
						className
					)}
					onClick={handleClick}
					asChild
				>
					<ConditionalAsChild asChild={asChild} fallback="span">
						{children ?? <Info autoSize />}
					</ConditionalAsChild>
				</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						side="top"
						align="center"
						sideOffset={4}
						// Our arrow is wide and we have rounded corners, so we need some buffer
						// from the edges to not show a gap between the arrow and tooltip corner.
						alignOffset={-8}
						className={cm(
							'data-[state=delayed-open]:animate-fade-in',
							TOOLTIP_CONTENT_CLASSNAME,
							contentClassName
						)}
						style={{ zIndex: Z_INDEX.tooltip }}
						{...rest}
					>
						{content}
						{!hideArrow && (
							<RadixTooltip.Arrow
								width={16}
								height={8}
								className="fill-tooltip-bg"
							/>
						)}
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
				<TapOverlay
					open={!!open}
					zIndex={Z_INDEX.tooltip - 1}
					onClose={() => onOpenChange(false)}
				/>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	);
}
